import { useEffect, useState } from 'react'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogCloseButton,
} from '@wartek-id/dialog-v1'

import { getPangkatGolongan } from 'app/KinerjaPage/utils/pangkat'
import { mapJenjangName } from 'app/KinerjaPage/utils/jenjang'

import type { HTMLAttributes } from 'react'
import type { KinerjaProfil } from 'types/kinerja'

import ImgAssessmentSubjectBook from 'assets/icons/img-assessment-subject-book.svg'

interface ConfirmDialogProps {
  profile: KinerjaProfil
  isOpen: boolean
  disabledConfirmButton?: boolean
  isLoading?: boolean
  onClose?: () => void
  onConfirm?: () => void
}

interface ConfirmListItemProps extends HTMLAttributes<HTMLDivElement> {
  label: string
  value: string
  testId?: string
}

function ConfirmListItem({
  label,
  value,
  className,
  ...props
}: ConfirmListItemProps) {
  return (
    <div className={clsx('flex gap-2', className)} {...props}>
      <Text variant="body" className="mb-1 w-48">
        {label}
      </Text>
      <Text variant="body-bold">:</Text>
      <Text variant="body-bold">{value}</Text>
    </div>
  )
}

export default function ConfirmDialog({
  profile,
  isOpen,
  onClose = () => {},
  onConfirm = () => {},
  disabledConfirmButton = false,
  isLoading = false,
}: ConfirmDialogProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(isOpen)

  useEffect(() => {
    setIsDialogOpen(isOpen)
  }, [isOpen])

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    onClose()
  }

  const handleConfirm = () => {
    onConfirm()
  }

  return (
    <Dialog isOpen={isDialogOpen} onClose={onClose}>
      <DialogOverlay />
      <DialogContent>
        <div className="inline-flex flex-col gap-4 pt-[60px] px-6">
          <ImgAssessmentSubjectBook width={84} height={84} />

          <Text variant="display-sm">Apakah data diri Anda sudah benar?</Text>

          <div className="flex flex-col gap-2">
            <ConfirmListItem label="Nama" value={profile.nama} />
            {profile.kategoriPegawai !== 'UNDEFINED' && (
              <ConfirmListItem
                data-testid="nip"
                label="NIP"
                value={profile.nip}
              />
            )}
            <ConfirmListItem
              data-testid="status-kepegawaian"
              label="Status Kepegawaian"
              value={profile.statusPegawai || '-'}
            />
            {profile.kategoriPegawai === 'PNS' && (
              <ConfirmListItem
                data-testid="pangkat"
                label="Pangkat, Golongan Ruang"
                value={getPangkatGolongan(profile.pangkatGolongan)}
              />
            )}
            {profile.kategoriPegawai !== 'UNDEFINED' && (
              <ConfirmListItem
                data-testid="jenjang"
                label="Jenjang Jabatan"
                value={mapJenjangName(profile.jenjang)}
              />
            )}
            <ConfirmListItem
              label="Satuan Pendidikan Induk"
              value={profile.satuanPendidikan}
            />
          </div>

          <Text>
            Saat ini data hanya bisa diedit satu kali saja. Pastikan sesuai, ya.
          </Text>
        </div>
        <div className="flex p-6 mt-3 gap-5">
          <Button
            disabled={disabledConfirmButton}
            isLoading={isLoading}
            size="lg"
            onClick={handleConfirm}
          >
            Data sudah benar
          </Button>
          <Button size="lg" color="white" onClick={handleDialogClose}>
            Cek ulang
          </Button>
        </div>
        <DialogCloseButton />
      </DialogContent>
    </Dialog>
  )
}
